import { useTranslation } from 'react-i18next';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { ORDER_METHODS, REDUX_STORE_SLICES } from '../../services/exports/Constants';
import moment from 'moment';
import useOrderMethodsStatus from '../../hooks/availability/useOrderMethodsStatus';
import useCompanyLocation from '../../hooks/location/useCompanyLocation';
import HelperMethods from '../../services/exports/HelperMethods';
import useOrderPreparationTime from '../../hooks/order/useOrderPreparationTime';
import AddressHelpers from '../../services/helpers/AddressHelpers';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import classnames from 'classnames';
import { FC, ReactElement, useMemo, useState } from 'react';
import useOpeningStatus from '../../hooks/availability/useOpeningStatus';
import CompanyDetailsModal from '../../modals/CompanyDetailsModal';
import useStore from '../../hooks/store/useStore';
import { Card, CardContent } from '../ui/card';
import PickupIcon from '../../assets/icons/order-methods/PickupIcon';
import RoomServiceIcon from '../../assets/icons/order-methods/RoomServiceIcon';
import DeliveryIcon from '../../assets/icons/order-methods/DeliveryIcon';
import useTheme from '../../hooks/ui/useTheme';

interface Props {
  className?: string;
  type?: 1 | 2;
}

type InfoRowProps = {
  withChangeButton?: boolean;
  className?: string;
};

const OrderType: FC<Props> = ({ className, type }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:OrderType:OrderType' });
  const theme = useTheme();
  const { localData } = useOrderFlow();
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const getPreparationTime = useOrderPreparationTime(company);
  const { formatDateTime, createScheduledOrderSlot, toggleOrderMethodModal } = useAppMethods();
  const { isOrderMethodCurrentlyOpen, isOrderMethodOpenForFutureOrders } = useOrderMethodsStatus();
  const openingStatus = useOpeningStatus();
  const [showCompanyDetailsModal, setShowCompanyDetailsModal] = useState<boolean>(false);

  const location = useCompanyLocation({
    datetime: localData.scheduled_for === null ? null : localData.scheduled_for,
  });

  const getScheduledTime = (): string => {
    if (localData.scheduled_for === null) {
      return t('asap');
    }

    return moment(localData.scheduled_for).isSame(moment(), 'day')
      ? createScheduledOrderSlot(localData.scheduled_for)
      : formatDateTime(localData.scheduled_for, { format: 'dd, DD MMM HH:mm' });
  };

  const renderPreparationTime = (method: ORDER_METHODS) => {
    const isMethodAvailable = isOrderMethodCurrentlyOpen(method) || isOrderMethodOpenForFutureOrders(method);

    if (!isMethodAvailable) {
      return t('unavailable');
    }

    if (localData.scheduled_for) {
      return getScheduledTime();
    }

    return t('time', {
      time: HelperMethods.formatTimeInterval(getPreparationTime(method)),
    });
  };

  const getAddress = (): string => {
    if (localData.method === ORDER_METHODS.DELIVERY) {
      return AddressHelpers.setCountry(location?.country).format({
        street_name: localData?.delivery_street_name,
        city: localData?.delivery_city,
        zip_code: localData?.delivery_zip_code,
      });
    }

    return AddressHelpers.setCountry(location?.country).format({
      street_name: location?.address,
      city: location?.city,
      zip_code: location?.zip_code,
    });
  };

  const OrderMethodIcon = useMemo(
    () =>
      ({
        [ORDER_METHODS.DELIVERY]: DeliveryIcon,
        [ORDER_METHODS.PICKUP]: PickupIcon,
        [ORDER_METHODS.ROOM_SERVICE]: RoomServiceIcon,
      })[localData.method ?? ORDER_METHODS.PICKUP],
    [localData.method],
  );

  const OrderMethod = ({ withChangeButton, className }: InfoRowProps): ReactElement => {
    return (
      <div className="flex justify-between">
        <div className={classnames('flex divide-x divide-brand-border text-sm', className)}>
          <div className="pr-2">
            <span>{t(`order_methods.${localData.method}`)}</span>
          </div>
          {!localData?.scheduled_for && (
            <div className="px-2 pl-2">
              <span>ASAP</span>
            </div>
          )}
          <div className="px-2 pl-2">
            <span>{renderPreparationTime(localData.method)}</span>
          </div>
        </div>
        {withChangeButton && (
          <button
            className="text-brand-primary text-xs"
            onClick={() => toggleOrderMethodModal({ openModal: true, orderMethod: localData.method })}
          >
            {t('buttons.change')}
          </button>
        )}
      </div>
    );
  };

  const CompanyName = ({ className }: InfoRowProps): ReactElement => {
    return (
      <div className="flex justify-between gap-sm items-start text-xs w-full">
        <div>
          <p className={classnames('text-sm', className)}>{getAddress()}</p>
          <p className={classnames('text-sm', className)}>{company?.name}</p>
        </div>
      </div>
    );
  };

  const renderCompanyDetailsModal = useMemo<JSX.Element>(() => {
    return (
      <CompanyDetailsModal
        data={company}
        openModal={showCompanyDetailsModal}
        toggleModal={() => setShowCompanyDetailsModal(false)}
      />
    );
  }, [company, showCompanyDetailsModal]);

  switch (type) {
    case 2:
      return (
        <>
          <div
            className={classnames(
              'border border-brand-border px-md rounded-lg divide-y divide-brand-border',
              className,
            )}
          >
            <div className="flex justify-between py-sm gap-x-sm">
              <div>
                <OrderMethod className="text-sm font-normal" />
                <CompanyName className="text-sm font-normal" />
              </div>
              <button
                className="underline text-sm h-fit"
                onClick={() => toggleOrderMethodModal({ openModal: true, orderMethod: localData.method })}
              >
                {t('buttons.change')}
              </button>
            </div>
            <div className="flex justify-between py-sm gap-x-sm">
              <p className="text-sm font-normal">{openingStatus.details_text}</p>
              <button className="underline text-sm" onClick={() => setShowCompanyDetailsModal(true)}>
                {t('buttons.see_hours')}
              </button>
            </div>
          </div>
          {renderCompanyDetailsModal}
        </>
      );
    default:
      return (
        <Card className="border-brand-border">
          <CardContent className="p-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <OrderMethodIcon color={theme.color.brand.primary} className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-semibold mb-1">{t(`order_methods.${localData.method}`)}</p>
                  <CompanyName className="text-xs text-brand-text-secondary font-normal" />
                  <div className="flex items-center gap-x-sm font-medium text-brand-primary mt-xs">
                    {!localData?.scheduled_for ? (
                      <span>ASAP · {renderPreparationTime(localData.method)}</span>
                    ) : (
                      <span>{renderPreparationTime(localData.method)}</span>
                    )}
                  </div>
                </div>
              </div>
              <button
                className="text-brand-primary text-xs underline"
                onClick={() => toggleOrderMethodModal({ openModal: true, orderMethod: localData.method })}
              >
                {t('buttons.change')}
              </button>
            </div>
          </CardContent>
        </Card>
      );
  }
};

export default OrderType;
